/**
 * D3-based network graph.
 *
 * MNIST has 784 inputs, so input neurons are never drawn individually: they are
 * summarized as a single square "input tile" node whose thickness represents 784
 * connections. Hidden and output neurons are drawn one per node.
 *
 * The tile can display the current training sample, stacked on faded copies of
 * itself: the network is fed batches, not single images, and a lone tile would
 * suggest otherwise.
 *
 * This module owns the SVG. It reads snapshots only; it never mutates the model.
 */
import * as d3 from 'd3';
import type { NetworkSnapshot } from '../engine/types.ts';
import type { EdgePalette } from './edge-palette.ts';
import {
    EDGE_PALETTE_DESCRIPTIONS,
    FOCUS_PALETTE_DESCRIPTIONS,
    OUTPUT_COLORMAP_DESCRIPTION,
    edgeStyle,
    focusRamp,
    outputColor,
    outputRamp,
    paletteRamp,
} from './edge-palette.ts';
import { pixelSensitivity } from './input-focus.ts';
import { focusImageUrl, mnistImageUrl } from './mnist-tile.ts';

/**
 * Edge length of the square input block, in SVG units.
 *
 * Large enough that the per-pixel focus overlay reads as a 28x28 field rather
 * than as texture: the tile is the only place a receptive field can be shown, so
 * its resolution is a functional requirement, not decoration.
 */
const INPUT_TILE_SIZE = 66;
/** Cards behind the input tile; purely illustrative, not the configured batch size. */
const BATCH_STACK_DEPTH = 3;
/** Up-left offset per card, small enough that the front sample stays dominant. */
const BATCH_STACK_OFFSET = 4;
/** Hidden nodes shrink with layer width; outputs keep a fixed, larger size. */
const HIDDEN_NODE_MAX_RADIUS = 9;
const OUTPUT_NODE_RADIUS = 8;
/** One step up from 10 so the digits read at a glance; matches the column captions. */
const OUTPUT_LABEL_FONT_SIZE = 11;
/**
 * Distance from an output node's centre to its digit label. The old gap left 7
 * units of air past the node radius; that space now holds the winner arrow, and
 * every row keeps the same offset so the digits stay in one column.
 */
const OUTPUT_LABEL_OFFSET = 25;
/** Left-pointing arrow sitting just right of the winning output node. */
const OUTPUT_WINNER_MARKER = '\u2190';
const OUTPUT_WINNER_MARKER_OFFSET = 10;
/**
 * Right edge of the percentage column, measured from the digit label. Previously
 * ~20 units sat between a digit and its "0%"; 60 roughly doubles that while
 * still fitting "100%" at the larger label size.
 */
const OUTPUT_PERCENT_COLUMN = 60;
/**
 * Selection focus. Only edges are dimmed: nodes, the input tile and the output
 * labels stay at full strength so the topology remains readable while one neuron
 * is inspected, and the selected neuron is marked by a halo instead.
 *
 * The fade is deliberately harsh and has no floor. With many neurons every other
 * edge becomes visual noise, and the point of the selection is the selected
 * neuron's own fan: an unrelated edge that fades to nothing is the intended
 * outcome, not a defect. The selection is a filter, not a comparison.
 */
const FADED_EDGE_FACTOR = 0.05;
/**
 * Edges touching the selection are promoted instead of merely left alone. Kept
 * modest so the halo stays the primary marker; the contrast against the faded
 * remainder already does most of the work.
 */
const FOCUSED_EDGE_BOOST = 1.4;
/** Ring gap between the selected node's edge and its halo, in SVG units. */
const HALO_RADIUS_GAP = 8;
/** Selection accent: the same blue as the selected switch, not a new shade. */
const HALO_COLOR = '#2563eb';
const HALO_GRADIENT_ID = 'selection-halo-glow';
/**
 * The input tile → first layer edges are a flow indicator, not a weight readout:
 * the tile aggregates 784 connections, so a weight averaged over them would say
 * nothing useful. They therefore carry fixed styling and are never dimmed.
 */
const INPUT_EDGE_COLOR = '#94a3b8';
const INPUT_EDGE_OPACITY = 0.7;
const INPUT_EDGE_WIDTH = 1;
/** Arrowhead sits just outside the target node so it is not painted over. */
const INPUT_ARROW_GAP = 2;
const INPUT_ARROW_ID = 'input-edge-arrow';
/** Caption baseline below the tile's front face. */
const INPUT_LABEL_OFFSET = 16;
/**
 * Rows in a layer are packed into this fraction of the available height. Ten
 * percent tighter than the full pane keeps a tall layer from using air it does
 * not need, while the centring keeps the columns aligned.
 */
const LAYER_ROW_SPACING = 0.9;

export interface GraphNode {
  id: string;
  layerIndex: number;
  /** Neuron index for drawable nodes; -1 for the aggregated input block. */
  unitIndex: number;
  kind: 'input' | 'hidden' | 'output';
}

export interface GraphLink {
  source: string;
  target: string;
  /** Mean weight of the represented connections; sign is preserved. */
  weight: number;
}

/** A tile → first-layer edge; styled by constant, never by weight. */
export interface GraphInputLink {
  target: string;
}

export interface GraphModel {
  nodes: GraphNode[];
  links: GraphLink[];
  /** Drawn separately from `links`, because they are not weight-encoded. */
  inputLinks: GraphInputLink[];
  inputSize: number;
}

/** A link with its weight already resolved onto the active palette. */
type StyledLink = GraphLink & { style: ReturnType<typeof edgeStyle> };

/** Collapses per-neuron weights into the aggregated input block. */
export function toGraphModel(snapshot: NetworkSnapshot): GraphModel {
  const nodes: GraphNode[] = [];
  const links: GraphLink[] = [];

  snapshot.layers.forEach((layer, layerIndex) => {
    layer.neuronIds.forEach((id, unitIndex) => {
      nodes.push({
        id,
        layerIndex: layerIndex + 1,
        unitIndex,
        kind: layer.id === 'out' ? 'output' : 'hidden',
      });
    });
  });

  const first = snapshot.layers[0];
  const inputSize = first?.inputIds.length ?? 0;
  nodes.unshift({ id: 'input', layerIndex: 0, unitIndex: -1, kind: 'input' });

  const inputLinks: GraphInputLink[] = [];

  snapshot.layers.forEach((layer, layerIndex) => {
    // The first layer is fed by the aggregated tile rather than by drawn neurons,
    // so it gets flow edges instead of weight-encoded ones.
    if (layerIndex === 0) {
      layer.neuronIds.forEach((targetId) => {
        inputLinks.push({ target: targetId });
      });
      return;
    }

    const sourceIds = snapshot.layers[layerIndex - 1]?.neuronIds ?? [];
    layer.neuronIds.forEach((targetId, unit) => {
      sourceIds.forEach((sourceId, input) => {
        const row = layer.kernel[input];
        links.push({ source: sourceId, target: targetId, weight: row?.[unit] ?? 0 });
      });
    });
  });

  return { nodes, links, inputLinks, inputSize };
}

/** Maximum absolute weight, used to normalize stroke width/opacity. */
export function weightScale(model: GraphModel): number {
  return model.links.reduce((max, link) => Math.max(max, Math.abs(link.weight)), 0) || 1;
}

/** Two significant digits; very small weights switch to exponent form to stay readable. */
function formatWeight(value: number): string {
  return value >= 0.01 ? value.toFixed(2) : value.toExponential(1);
}

export interface NetworkGraphOptions {
  width: number;
  height: number;
  /** Optional legend shell; the graph fills in its gradient and labels. */
  legend?: HTMLElement | null;
  /** Initial colour ramp; the UI may change it later through `setPalette`. */
  palette?: EdgePalette;
  onSelect?: (node: GraphNode | null) => void;
}

export class NetworkGraph {
  #svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  #options: NetworkGraphOptions;
  #host: HTMLElement;
  #width: number;
  #height: number;
  #snapshot: NetworkSnapshot | null = null;
  #inputPosition: { x: number; y: number } | null = null;
  #inputPixels: Float32Array | null = null;
  #inputLabel: number | null = null;
  #palette: EdgePalette;
  #outputRows: Array<{ unit: number; x: number; y: number }> = [];
  #predictions: Float32Array | null = null;
  /** Id of the neuron the visitor is inspecting, or `null` for "show everything". */
  #selected: string | null = null;
  /** Signed, max-normalized pixel field of the selected neuron; `null` when none applies. */
  #focus: Float32Array | null = null;

  constructor(host: HTMLElement, options: NetworkGraphOptions) {
    this.#options = options;
    this.#palette = options.palette ?? 'red-green';
    this.#host = host;
    this.#width = Math.max(240, Math.round(host.clientWidth || options.width));
    this.#height = Math.max(320, Math.round(host.clientHeight || options.height));
    this.#svg = d3
      .select(host)
      .append('svg')
      .attr('viewBox', `0 0 ${String(this.#width)} ${String(this.#height)}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr('class', 'network-graph')
      .attr('role', 'img')
      .attr('aria-label', 'Neural network topology');

    // The pane drives the viewBox so stroke widths and node spacing stay in real
    // pixels; scaling a fixed viewBox would distort both at extreme pane sizes.
    new ResizeObserver(() => {
      this.#fit();
    }).observe(host);
    window.addEventListener('resize', () => this.#fit());
  }

  #fit(): void {
    const { clientWidth, clientHeight } = this.#host;
    if (clientWidth <= 0) {
      return;
    }
    const width = Math.max(240, Math.round(clientWidth));
    const height = Math.max(320, Math.round(clientHeight || this.#options.height));
    if (width === this.#width && height === this.#height) {
      return;
    }
    this.#width = width;
    this.#height = height;
    this.#svg.attr('viewBox', `0 0 ${String(width)} ${String(height)}`);
    if (this.#snapshot) {
      this.render(this.#snapshot);
    }
  }

  render(snapshot: NetworkSnapshot): void {
    this.#snapshot = snapshot;
    this.#fit();
    const model = toGraphModel(snapshot);
    const width = this.#width;
    const height = this.#height;
    const palette = this.#palette;

    const maxPerLayer = Math.max(...snapshot.layers.map((layer) => layer.neuronIds.length), 1);
    const layerCount = snapshot.layers.length + 1;

    // The right margin has to hold the output digit plus its percentage column;
    // the winner arrow sits inside that span, between node and digit.
    const rightMargin = OUTPUT_LABEL_OFFSET + OUTPUT_PERCENT_COLUMN + 12;
    const x = (layerIndex: number): number =>
      ((layerIndex + 1) / (layerCount + 1)) * (width - rightMargin - 40) + 40;

    const yFor = (unit: number, count: number): number => {
      const usable = (height - 60) * LAYER_ROW_SPACING;
      const step = usable / Math.max(count, 1);
      // The spare height is split evenly above and below, so tightening the rows
      // keeps the layer centred rather than sliding it up.
      const top = 30 + (height - 60 - usable) / 2;
      return top + step * (unit + 0.5);
    };

    const positions = new Map<string, { x: number; y: number }>();
    model.nodes.forEach((node) => {
      const layer = snapshot.layers[node.layerIndex - 1];
      const count = node.kind === 'input' ? 1 : (layer?.neuronIds.length ?? 1);
      positions.set(node.id, {
        x: x(node.layerIndex),
        y: node.kind === 'input' ? height / 2 : yFor(node.unitIndex, count),
      });
    });

    // Growth only ever appends ids, but a reset can rebuild an architecture that
    // no longer contains the selected one; a stale id would fade the whole graph.
    if (this.#selected && !model.nodes.some((node) => node.id === this.#selected)) {
      this.#selected = null;
    }

    const scale = weightScale(model);
    this.#renderLegend(scale);

    this.#svg.selectAll('*').remove();

    // Styles are resolved once per link rather than once per attribute callback:
    // three passes over ~8k links would recompute the same ramp three times.
    const styledLinks: StyledLink[] = model.links.map((link) => ({
      ...link,
      style: edgeStyle(link.weight, scale, palette),
    }));

    const linkGroup = this.#svg.append('g').attr('class', 'links');
    linkGroup
      .selectAll('line')
      .data(styledLinks)
      .join('line')
      .attr('x1', (link) => positions.get(link.source)?.x ?? 0)
      .attr('y1', (link) => positions.get(link.source)?.y ?? 0)
      .attr('x2', (link) => positions.get(link.target)?.x ?? 0)
      .attr('y2', (link) => positions.get(link.target)?.y ?? 0)
      .attr('stroke', (link) => link.style.color)
      .attr('stroke-width', (link) => link.style.width);

    // Tile → first layer is a separate group so weight dimming never reaches it and
    // the constant styling stays the single source of its appearance.
    const hiddenRadius = Math.min(HIDDEN_NODE_MAX_RADIUS, 90 / maxPerLayer);
    const inputAnchor = positions.get('input');
    this.#svg
      .append('g')
      .attr('class', 'input-links')
      .selectAll('line')
      .data(inputAnchor ? model.inputLinks : [])
      .join('line')
      // Every input edge shares the tile's right edge as its origin.
      .attr('x1', () => (inputAnchor?.x ?? 0) + INPUT_TILE_SIZE / 2)
      .attr('y1', () => inputAnchor?.y ?? 0)
      // Stop short of the node so the arrowhead sits beside it, not under it.
      .attr('x2', (link) => (positions.get(link.target)?.x ?? 0) - hiddenRadius - INPUT_ARROW_GAP)
      .attr('y2', (link) => positions.get(link.target)?.y ?? 0)
      .attr('stroke', INPUT_EDGE_COLOR)
      .attr('stroke-opacity', INPUT_EDGE_OPACITY)
      .attr('stroke-width', INPUT_EDGE_WIDTH)
      .attr('marker-end', `url(#${INPUT_ARROW_ID})`);

    // The halo sits between the edges and the nodes, so it reads as a glow behind
    // the selected node rather than as a second outline painted over it.
    const defs = this.#svg.append('defs');
    defs
      .append('radialGradient')
      .attr('id', HALO_GRADIENT_ID)
      .selectAll('stop')
      .data([
        { offset: '40%', opacity: 0.55 },
        { offset: '100%', opacity: 0 },
      ])
      .join('stop')
      .attr('offset', (stop) => stop.offset)
      .attr('stop-color', HALO_COLOR)
      .attr('stop-opacity', (stop) => stop.opacity);
    // `markerUnits` defaults to the stroke width, so the head keeps its proportion
    // to the constant 1px input edge.
    defs
      .append('marker')
      .attr('id', INPUT_ARROW_ID)
      .attr('viewBox', '0 0 10 10')
      .attr('refX', 9)
      .attr('refY', 5)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,0 L10,5 L0,10 z')
      .attr('fill', INPUT_EDGE_COLOR);
    this.#svg.append('g').attr('class', 'selection-halo');

    const nodeGroup = this.#svg.append('g').attr('class', 'nodes');
    nodeGroup
      .selectAll('circle')
      .data(model.nodes.filter((node) => node.kind !== 'input'))
      .join('circle')
      .attr('class', (node) => (node.kind === 'output' ? 'output-node' : 'hidden-node'))
      .attr('cx', (node) => positions.get(node.id)?.x ?? 0)
      .attr('cy', (node) => positions.get(node.id)?.y ?? 0)
      .attr('r', (node) => (node.kind === 'output' ? OUTPUT_NODE_RADIUS : hiddenRadius))
      // Output fill is probability, applied by `#paintOutputs`; without a
      // prediction it rests at the ramp's low end rather than implying a value.
      .attr('fill', (node) => (node.kind === 'output' ? outputColor(0) : '#f59e0b'))
      .attr('stroke', '#0f172a')
      .attr('stroke-width', 1)
      .style('cursor', 'pointer')
      .on('click', (_event, node: GraphNode) => {
        this.#select(node);
      });

    this.#inputPosition = positions.get('input') ?? null;
    this.#svg.append('g').attr('class', 'input-tile');
    // Resolved before the tile is painted, because the tile is what shows the
    // receptive field of the neuron restored across a re-render.
    this.#updateFocus();
    this.#renderInputTile();

    this.#outputRows = model.nodes
      .filter((node) => node.kind === 'output')
      .map((node) => ({
        unit: node.unitIndex,
        x: positions.get(node.id)?.x ?? 0,
        y: positions.get(node.id)?.y ?? 0,
      }));
    this.#svg.append('g').attr('class', 'output-labels');
    this.#renderOutputs();

    // One caption row under the graph names what each column holds. Placing them
    // on a common baseline is what makes the three zones read as zones.
    const hiddenXs = snapshot.layers
      .map((layer, layerIndex) => (layer.id === 'out' ? null : x(layerIndex + 1)))
      .filter((value): value is number => value !== null);
    const firstHidden = hiddenXs[0] ?? x(1);
    const lastHidden = hiddenXs.at(-1) ?? firstHidden;

    this.#svg
      .append('g')
      .attr('class', 'column-labels')
      .selectAll('text')
      .data([
        { x: x(0), label: 'input' },
        { x: (firstHidden + lastHidden) / 2, label: 'network layers' },
        { x: x(snapshot.layers.length), label: 'output' },
      ])
      .join('text')
      .attr('x', (column) => column.x)
      .attr('y', height - 10)
      .attr('text-anchor', 'middle')
      .attr('font-size', 11)
      .attr('fill', '#64748b')
      .text((column) => column.label);

    this.#applySelection();
  }

  /**
   * Selects a neuron, or clears the selection when the same one is clicked again.
   *
   * The graph owns the focus state because it is the only thing that can draw it;
   * `onSelect` exists so the page can describe the choice in the inspector.
   */
  #select(node: GraphNode | null): void {
    const next = node?.id ?? null;
    this.#selected = next === this.#selected ? null : next;
    this.#applySelection();
    this.#updateFocus();
    this.#renderInputTile();
    this.#options.onSelect?.(this.#selected === null ? null : node);
  }

  /**
   * Resolves the selected neuron's pixel field.
   *
   * Recomputed whenever either input changes — the selection or the snapshot —
   * because growth rewrites the kernels the field is derived from. Cleared with
   * the selection so an unselected graph never shows a stale field.
   */
  #updateFocus(): void {
    this.#focus =
      this.#snapshot && this.#selected ? pixelSensitivity(this.#snapshot, this.#selected) : null;
  }

  /**
   * Applies the selection: dims the unrelated edges and rings the selected node.
   *
   * Nothing else changes, so the graph keeps its full contrast and the visitor can
   * still compare the focused neuron against the rest of the network. Run after
   * every render because the SVG is rebuilt wholesale.
   */
  #applySelection(): void {
    const selected = this.#selected;

    this.#svg
      .selectAll<SVGLineElement, StyledLink>('g.links line')
      .attr('stroke-opacity', (link) => this.#selectedOpacity(link, selected));

    // The halo borrows the drawn circle's geometry instead of recomputing it, so
    // a change to node sizing cannot leave the ring misaligned. Read through the
    // attributes: `cx.baseVal` is a float32, which would drift the halo a pixel
    // off its node on fractional positions.
    const node =
      selected === null
        ? null
        : (this.#svg
            .selectAll<SVGCircleElement, GraphNode>('g.nodes circle')
            .filter((candidate) => candidate.id === selected)
            .node() ?? null);
    const geometry = (target: SVGCircleElement, attribute: 'cx' | 'cy' | 'r'): number =>
      Number(target.getAttribute(attribute) ?? 0);

    this.#svg
      .select<SVGGElement>('g.selection-halo')
      .selectAll('circle')
      .data(node ? [node] : [])
      .join('circle')
      .attr('cx', (target) => geometry(target, 'cx'))
      .attr('cy', (target) => geometry(target, 'cy'))
      .attr('r', (target) => geometry(target, 'r') + HALO_RADIUS_GAP)
      .attr('fill', `url(#${HALO_GRADIENT_ID})`)
      .attr('stroke', HALO_COLOR)
      .attr('stroke-opacity', 0.9)
      .attr('stroke-width', 2)
      // The halo is decoration; it must not steal clicks from the node it rings.
      .attr('pointer-events', 'none');
  }

  /**
   * Opacity of one edge under the current selection: promoted when it touches the
   * selected neuron, faded hard when it does not, and the weight's own value when
   * nothing is selected.
   */
  #selectedOpacity(link: StyledLink, selected: string | null): number {
    const base = link.style.opacity;
    if (selected === null) {
      return base;
    }
    if (link.source === selected || link.target === selected) {
      return Math.min(1, base * FOCUSED_EDGE_BOOST);
    }
    return base * FADED_EDGE_FACTOR;
  }

  /**
   * Shows the class probabilities for the sample currently in the input tile.
   *
   * Kept separate from `render()` because the weights of a snapshot do not
   * change: only the percentages and output fills do, and re-drawing a thousand
   * link lines twice a second to update them would be wasteful.
   */
  setPredictions(probabilities: Float32Array | null): void {
    this.#predictions = probabilities;
    this.#renderOutputs();
  }

  /** Probabilities aligned to the drawn output rows, or `null` when unusable. */
  #outputProbabilities(): Float32Array | null {
    // A probability array from another architecture would mislabel rows, so it
    // is dropped rather than rendered against the wrong units.
    return this.#predictions && this.#predictions.length >= this.#outputRows.length
      ? this.#predictions
      : null;
  }

  /** Repaints the output nodes and their digit/percentage labels from `#predictions`. */
  #renderOutputs(): void {
    const probabilities = this.#outputProbabilities();
    this.#paintOutputNodes(probabilities);
    this.#renderOutputLabels(probabilities);
  }

  /**
   * Fills each output node with its class probability on the magma ramp.
   *
   * Re-selected by unit rather than cached as a selection so a re-render that
   * rebuilt the circles cannot leave this painting detached nodes.
   */
  #paintOutputNodes(probabilities: Float32Array | null): void {
    this.#svg
      .selectAll<SVGCircleElement, GraphNode>('circle.output-node')
      .attr('fill', (node) => outputColor(probabilities?.[node.unitIndex] ?? 0));
  }

  #renderOutputLabels(probabilities: Float32Array | null): void {
    const group = this.#svg.select<SVGGElement>('g.output-labels');
    if (group.empty()) {
      return;
    }

    let bestUnit = -1;
    let bestProbability = -Infinity;
    if (probabilities) {
      this.#outputRows.forEach((row) => {
        const probability = probabilities[row.unit] ?? 0;
        if (probability > bestProbability) {
          bestProbability = probability;
          bestUnit = row.unit;
        }
      });
    }

    const labelX = (row: { x: number }): number => row.x + OUTPUT_LABEL_OFFSET;

    group
      .selectAll('text.output-digit')
      .data(this.#outputRows)
      .join('text')
      .attr('class', 'output-digit')
      .attr('x', labelX)
      .attr('y', (row) => row.y + 4)
      .attr('font-size', OUTPUT_LABEL_FONT_SIZE)
      .attr('fill', '#334155')
      .attr('font-weight', (row) => (row.unit === bestUnit ? 700 : 400))
      .text((row) => String(row.unit));

    // Right-aligned so the numbers form a column and do not jitter as they
    // change from 7% to 100%.
    group
      .selectAll('text.output-percent')
      .data(probabilities ? this.#outputRows : [])
      .join('text')
      .attr('class', 'output-percent')
      .attr('x', (row) => labelX(row) + OUTPUT_PERCENT_COLUMN)
      .attr('y', (row) => row.y + 4)
      .attr('text-anchor', 'end')
      .attr('font-size', OUTPUT_LABEL_FONT_SIZE)
      .attr('fill', (row) => (row.unit === bestUnit ? '#047857' : '#94a3b8'))
      .attr('font-weight', (row) => (row.unit === bestUnit ? 700 : 400))
      .text((row) => `${((probabilities?.[row.unit] ?? 0) * 100).toFixed(0)}%`);

    // The arrow points from the winning row's labels back at its node, so the
    // eye lands on the highlighted percentage knowing which neuron produced it.
    group
      .selectAll('text.output-winner')
      .data(this.#outputRows.filter((row) => row.unit === bestUnit))
      .join('text')
      .attr('class', 'output-winner')
      .attr('x', (row) => row.x + OUTPUT_WINNER_MARKER_OFFSET)
      .attr('y', (row) => row.y + 4)
      .attr('font-size', OUTPUT_LABEL_FONT_SIZE)
      .attr('text-anchor', 'start')
      .attr('fill', '#047857')
      .attr('font-weight', 700)
      .text(OUTPUT_WINNER_MARKER);
  }

  /**
   * Switches the colour ramp used for edge weights and repaints the graph.
   *
   * Only the SVG is redrawn, not the model: the palette is a pure presentation
   * choice and must not touch the snapshot the UI holds.
   */
  setPalette(palette: EdgePalette): void {
    if (palette === this.#palette) {
      return;
    }
    this.#palette = palette;
    if (this.#snapshot) {
      this.render(this.#snapshot);
    } else {
      this.#renderLegend(0);
    }
  }
  /**
   * Paints both legend rows: the switchable edge ramp with its weight endpoints,
   * and the fixed output ramp with its probability endpoints.
   *
   * A scale of 0 means "no weights yet", and the weight labels stay blank.
   */
  #renderLegend(scale: number): void {
    const legend = this.#options.legend;
    if (!legend) {
      return;
    }

    const edgeRamp = legend.querySelector<HTMLElement>('.edge-legend-ramp');
    if (edgeRamp) {
      edgeRamp.style.backgroundImage = `linear-gradient(to right, ${paletteRamp(this.#palette).join(', ')})`;
    }

    const outRamp = legend.querySelector<HTMLElement>('.output-legend-ramp');
    if (outRamp) {
      outRamp.style.backgroundImage = `linear-gradient(to right, ${outputRamp().join(', ')})`;
    }

    // The pixel overlay reuses the edge ramp, so this row changes with the
    // palette switch rather than staying fixed like the output row.
    const focusLegend = legend.querySelector<HTMLElement>('.focus-legend-ramp');
    if (focusLegend) {
      focusLegend.style.backgroundImage = `linear-gradient(to right, ${focusRamp(this.#palette).join(', ')})`;
    }

    // The endpoint colours are not self-explanatory for viridis or magma, so the
    // legend carries both readings. The visible captions are repeated in the
    // accessible name because `aria-label` would otherwise replace them.
    const edge = EDGE_PALETTE_DESCRIPTIONS[this.#palette];
    const focus = FOCUS_PALETTE_DESCRIPTIONS[this.#palette];
    const description = `Edges: ${edge}. Output: ${OUTPUT_COLORMAP_DESCRIPTION}. Pixels: ${focus}.`;
    legend.title = description;
    legend.setAttribute('role', 'img');
    legend.setAttribute('aria-label', description);

    const low = legend.querySelector<HTMLElement>('.edge-legend-low');
    const high = legend.querySelector<HTMLElement>('.edge-legend-high');
    if (low) {
      low.textContent = scale > 0 ? `-${formatWeight(scale)}` : '-';
    }
    if (high) {
      high.textContent = scale > 0 ? `+${formatWeight(scale)}` : '-';
    }
  }

  /**
   * Shows a normalized MNIST sample inside the input tile; `null` clears it.
   *
   * The label is the sample's ground truth, so the visitor can tell whether the
   * network's probabilities are right rather than only what it guessed.
   *
   * Only the tile is redrawn: re-rendering the whole graph would rebuild over a
   * thousand SVG lines twice a second for a purely decorative change.
   */
  setInputImage(pixels: Float32Array | null, label: number | null): void {
    // The raw pixels are kept rather than a rendered data URL, so that selecting
    // a neuron can re-render the same sample through the focus palette without
    // waiting for the worker to send it again.
    this.#inputPixels = pixels;
    this.#inputLabel = pixels ? label : null;
    this.#renderInputTile();
  }

  #renderInputTile(): void {
    const position = this.#inputPosition;
    if (!position) {
      return;
    }

    const group = this.#svg.select<SVGGElement>('g.input-tile');
    const size = INPUT_TILE_SIZE;

    // Deepest card first, so each nearer card covers the one behind it.
    const cards = Array.from(
      { length: BATCH_STACK_DEPTH },
      (_, index) => BATCH_STACK_DEPTH - index,
    );
    group
      .selectAll('rect.batch-card')
      .data(cards)
      .join('rect')
      .attr('class', 'batch-card')
      .attr('x', (depth) => position.x - size / 2 - depth * BATCH_STACK_OFFSET)
      .attr('y', (depth) => position.y - size / 2 - depth * BATCH_STACK_OFFSET)
      .attr('width', size)
      .attr('height', size)
      .attr('rx', 2)
      .attr('fill', '#0f172a')
      // Fading with depth reads as "more samples waiting behind this one".
      .attr('fill-opacity', (depth) => 0.5 / depth)
      // The stack is one control, so every card selects like the face does.
      .style('cursor', 'pointer')
      .on('click', () => {
        this.#select(null);
      });

    group
      .selectAll('rect.input-face')
      .data([position])
      .join('rect')
      .attr('class', 'input-face')
      .attr('x', (p) => p.x - size / 2)
      .attr('y', (p) => p.y - size / 2)
      .attr('width', size)
      .attr('height', size)
      .attr('rx', 2)
      .attr('fill', '#0f172a')
      .style('cursor', 'pointer')
      .on('click', () => {
        this.#select(null);
      });

    const image = this.#renderInputImage();
    group
      .selectAll('image')
      .data(image ? [image] : [])
      .join('image')
      .attr('href', (href) => href)
      .attr('x', position.x - size / 2 + 3)
      .attr('y', position.y - size / 2 + 3)
      .attr('width', size - 6)
      .attr('height', size - 6)
      .attr('preserveAspectRatio', 'none');

    // Ground-truth caption directly under the tile. Named rather than a bare "7"
    // so it cannot be mistaken for another output-class percentage.
    const label = this.#inputLabel;
    group
      .selectAll('text.input-label')
      .data(label === null ? [] : [label])
      .join('text')
      .attr('class', 'input-label')
      .attr('x', position.x)
      .attr('y', position.y + size / 2 + INPUT_LABEL_OFFSET)
      .attr('text-anchor', 'middle')
      .attr('font-size', OUTPUT_LABEL_FONT_SIZE)
      .attr('fill', '#334155')
      .text((value) => `label ${String(value)}`);
  }

  /**
   * The sample as a data URL, tinted with the selected neuron's receptive field.
   *
   * The field is drawn over the digit rather than instead of it: a hotspot that
   * is not tied to visible ink says nothing about which shape the neuron reacts
   * to. Without a selection — or for a neuron whose field is all zeros — the
   * sample is shown untouched.
   */
  #renderInputImage(): string | null {
    const pixels = this.#inputPixels;
    if (!pixels) {
      return null;
    }
    const focus = this.#focus;
    return focus ? focusImageUrl(pixels, focus, this.#palette) : mnistImageUrl(pixels);
  }
}

export { d3 };
